import React, { useEffect, useState } from "react";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { scheduleUndoableDelete } from "@/lib/undoDelete";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, PencilSimple, UploadSimple, Package, Warning, Trash } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function LogoImg({ path }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!path) return;
    api.get(`/files/${path}`, { responseType: "blob" })
      .then((r) => setSrc(URL.createObjectURL(r.data)))
      .catch(() => {});
  }, [path]);
  if (!path) return <div className="w-14 h-14 bg-secondary border border-border flex items-center justify-center text-xs overline">Logo</div>;
  if (!src) return <div className="w-14 h-14 bg-secondary animate-pulse" />;
  return <img src={src} alt="logo" className="w-14 h-14 object-contain bg-white border border-border" />;
}

export default function Products() {
  const [items, setItems] = useState([]);
  const [plants, setPlants] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nama: "", aktif: true, plant_id: "", min_reorder: "0", aliases: "" });

  const load = async () => {
    const [{ data: p }, { data: pl }, { data: al }] = await Promise.all([
      api.get("/products"),
      api.get("/plants", { params: { aktif: true } }),
      api.get("/products/alerts"),
    ]);
    setItems(p);
    setPlants(pl);
    setAlerts(al);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ nama: "", aktif: true, plant_id: "", min_reorder: "0", aliases: "" });
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      nama: p.nama,
      aktif: p.aktif,
      plant_id: p.plant_id || "",
      min_reorder: String(p.min_reorder || 0),
      aliases: (p.aliases || []).join(", "),
    });
    setOpen(true);
  };

  const onDelete = (p) => {
    scheduleUndoableDelete({
      items, setItems, target: p,
      label: `Produk "${p.nama}"`,
      apiCall: () => api.delete(`/products/${p.id}`, { params: { hard: true } }),
      onDone: load, // reload untuk refresh alerts
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        plant_id: form.plant_id || null,
        min_reorder: parseFloat(form.min_reorder || "0") || 0,
        aliases: (form.aliases || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      };
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Produk diperbarui");
      } else {
        await api.post("/products", payload);
        toast.success("Produk ditambahkan");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onUploadLogo = async (p, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/products/${p.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo diupload");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl">
      <div className="flex flex-col md:flex-row md:items-end justify-between border-b border-border pb-6 mb-6 gap-4">
        <div>
          <div className="overline">Master Data</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
            <span className="w-11 h-11 rounded-full bg-orange-100 text-primary flex items-center justify-center shrink-0">
              <Package size={22} weight="bold" />
            </span>
            Produk Semen
          </h1>
        </div>
        <Button onClick={openNew} className="rounded-lg font-bold uppercase tracking-wider" data-testid="add-product-button">
          <Plus size={16} className="mr-2" /> Tambah Produk
        </Button>
      </div>

      {alerts.length > 0 && (
        <div className="border border-amber-500/40 bg-amber-50 p-4 mb-6" data-testid="product-alerts-panel">
          <div className="flex items-center gap-2 text-amber-800 font-bold uppercase tracking-wider text-xs mb-3">
            <Warning size={16} weight="bold" /> {alerts.length} Produk Butuh Reorder
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {alerts.map((a) => (
              <div key={a.produk_id} className="border border-amber-300 bg-white p-2 text-xs" data-testid={`alert-${a.produk_id}`}>
                <div className="font-bold flex items-center justify-between">
                  <span>{a.produk_nama}</span>
                  <span className={`px-1.5 py-0.5 border text-[9px] uppercase font-bold tracking-wider ${a.severity === "critical" ? "border-red-500 text-red-700 bg-red-50" : "border-amber-500 text-amber-700 bg-amber-50"}`}>
                    {a.severity === "critical" ? "Kritis" : "Rendah"}
                  </span>
                </div>
                <div className="font-mono text-[10px] text-muted-foreground mt-1">
                  Sisa: <span className="font-bold text-foreground">{a.kuantiti_sisa}</span> / Min: <span className="font-bold text-foreground">{a.min_reorder}</span>
                  {" · "}<span>{a.so_aktif_count} SO aktif</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((p) => {
          const alertMatch = alerts.find((a) => a.produk_id === p.id);
          return (
          <div key={p.id} className="border border-border p-5 bg-card flex gap-4 items-center">
            <LogoImg path={p.logo_path} />
            <div className="flex-1">
              <div className="font-bold text-lg tracking-tight">{p.nama}</div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className={`status-pill inline-flex ${p.aktif ? "status-termuat" : "status-belum"}`}>
                  {p.aktif ? "AKTIF" : "NONAKTIF"}
                </span>
                {p.min_reorder > 0 && (
                  <span
                    className={`inline-flex items-center gap-1 border text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${alertMatch ? (alertMatch.severity === "critical" ? "border-red-500 text-red-700 bg-red-50" : "border-amber-500 text-amber-700 bg-amber-50") : "border-border text-muted-foreground"}`}
                    data-testid={`min-reorder-badge-${p.id}`}
                  >
                    {alertMatch && <Warning size={10} weight="bold" />}
                    Min Reorder: {p.min_reorder}
                  </span>
                )}
                {(p.aliases || []).length > 0 && (
                  <span
                    className="inline-flex items-center gap-1 border border-blue-400/50 bg-blue-50 text-blue-800 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5"
                    title={`Aliases: ${(p.aliases || []).join(", ")}`}
                    data-testid={`aliases-badge-${p.id}`}
                  >
                    {p.aliases.length} alias
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label className="cursor-pointer">
                <input type="file" accept="image/*" onChange={(e) => onUploadLogo(p, e.target.files?.[0])} className="hidden" data-testid={`upload-logo-${p.nama.replace(/\s/g, "-")}`} />
                <span className="inline-flex items-center px-3 py-2 border border-border text-xs font-bold uppercase tracking-wider hover:bg-secondary">
                  <UploadSimple size={14} className="mr-1" /> Logo
                </span>
              </label>
              <Button size="sm" variant="outline" className="rounded-lg" onClick={() => openEdit(p)} data-testid={`edit-product-${p.nama.replace(/\s/g, "-")}`}>
                <PencilSimple size={14} className="mr-1" /> Edit
              </Button>
              <Button size="sm" variant="outline" className="rounded-lg text-destructive bg-destructive/5 border-destructive/40"
                onClick={() => onDelete(p)}
                data-testid={`delete-product-${p.nama.replace(/\s/g, "-")}`}
                title="Hapus permanen">
                <Trash size={14} weight="fill" />
              </Button>
            </div>
          </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-lg max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Produk" : "Tambah Produk"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="product-form">
            <div>
              <Label className="overline block mb-2">Nama Produk *</Label>
              <Input value={form.nama} onChange={(e) => setForm((f) => ({ ...f, nama: e.target.value }))} required className="rounded-lg h-11" data-testid="input-product-nama" />
            </div>
            <div>
              <Label className="overline block mb-2">Default Packing Plant</Label>
              <Select
                value={form.plant_id || "__none__"}
                onValueChange={(v) => setForm((f) => ({ ...f, plant_id: v === "__none__" ? "" : v }))}
              >
                <SelectTrigger className="rounded-lg h-11" data-testid="select-product-plant">
                  <SelectValue placeholder="Pilih plant..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— tidak ada default —</SelectItem>
                  {plants.map((pl) => (
                    <SelectItem key={pl.id} value={pl.id}>
                      {pl.kode} · {pl.nama}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Saat produk ini dipilih di SPB, plant akan otomatis terisi (bisa diubah manual).
              </p>
            </div>
            <div>
              <Label className="overline block mb-2">Minimum Reorder (opsional)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.min_reorder}
                onChange={(e) => setForm((f) => ({ ...f, min_reorder: e.target.value }))}
                placeholder="0"
                className="rounded-lg h-11 font-mono"
                data-testid="input-product-min-reorder"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Ambang alert stok rendah. Sistem akan cek total sisa dari semua SO aktif produk ini, dan tandai jika &le; ambang ini.
              </p>
            </div>
            <div>
              <Label className="overline block mb-2">Aliases untuk Sync (opsional)</Label>
              <Input
                value={form.aliases}
                onChange={(e) => setForm((f) => ({ ...f, aliases: e.target.value }))}
                placeholder="SMN PCC DYX, DYX, Dynamix"
                className="rounded-lg h-11 font-mono"
                data-testid="input-product-aliases"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Nama alternatif produk ini di sistem lain (ERP/Sheet). Pisahkan dengan koma. Digunakan saat Sync Google Sheets untuk mencocokkan nama_barang yang beda format (mis. Sheet pakai kode ERP <span className="font-mono">SMN PCC DYX</span> untuk produk internal <span className="font-mono">Semen Dynamix</span>).
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.aktif} onCheckedChange={(v) => setForm((f) => ({ ...f, aktif: v }))} />
              <Label>Aktif</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-lg" onClick={() => setOpen(false)}>Batal</Button>
              <Button type="submit" className="rounded-lg font-bold uppercase" data-testid="save-product-button">Simpan</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
